import React, { useState } from 'react';
import { Car, ChevronDown, ChevronUp, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useFx } from '../../hooks/FxRatesContext';
import type { CarTrip, CarTripDay } from '../../types/CarTripTypes';
import { useTranslation } from '../../useTranslation';
import { formatCarTripDaysCount } from '../../utils/carTripUi';
import { TagBadge } from '../common/TagBadge';
import { TripRouteTile } from '../common/TripRouteTile';
import { TRIP_TILE_THEMES } from '../common/tripTileThemes';
import { CarRouteSep } from './CarRouteSep';

const cityClass =
  'text-base sm:text-lg font-semibold text-stone-900 dark:text-white leading-snug [overflow-wrap:anywhere] line-clamp-2';

export const CarTripCard: React.FC<{
  trip: CarTrip;
  selected?: boolean;
  onSelect?: () => void;
  onDelete?: (id: number) => void;
  onView?: (trip: CarTrip) => void;
  onEdit?: (trip: CarTrip) => void;
  onAddDay?: (trip: CarTrip) => void;
  isAuthenticated?: boolean;
}> = ({ trip, selected, onSelect, onDelete, onView, onEdit, onAddDay, isAuthenticated = true }) => {
  const [daysExpanded, setDaysExpanded] = useState(false);
  const settings = localStorage.getItem('travelDiarySettings')
    ? JSON.parse(localStorage.getItem('travelDiarySettings')!)
    : { language: 'ru-RU', distanceUnit: 'km' };
  const { t, formatDate, formatNumber, formatDistance } = useTranslation(settings.language);
  const { toDisplay, displaySymbol } = useFx();
  const theme = TRIP_TILE_THEMES.car;
  const days = trip.days ?? [];
  const firstDay = days[0];
  const lastDay = days[days.length - 1] ?? firstDay;
  const isMultiDay = days.length > 1;
  const hasWaypoints = (firstDay?.waypoints ?? []).some((waypoint) => waypoint.trim());
  const totalDistance = trip.totalDistance ?? days.reduce((total, day) => total + (day.distance || 0), 0);
  const totalCost = trip.totalCost ?? days.reduce((total, day) => total + (day.totalCost || 0), 0);
  const daysCountLabel = formatCarTripDaysCount(
    days.length,
    settings.language,
    t.carTripDay,
    t.carTripDaysCount,
  );
  const priceLabel = `${formatNumber(toDisplay(totalCost, 'RUB'))} ${displaySymbol}`;

  if (!firstDay) return null;

  const stop = (callback: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    callback();
  };

  const borderIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  );

  const renderLeg = (segmentIndex: number) => {
    const crossings = (firstDay.borderCrossings ?? []).filter(
      (crossing) => crossing.segmentIndex === segmentIndex,
    );
    if (crossings.length > 0) {
      return crossings.map((crossing, index) => (
        <div key={`${segmentIndex}-${index}`} className="mt-1">
          <p className="text-xs text-stone-500 dark:text-slate-400">
            {formatDistance(crossing.distanceToBorder, settings.distanceUnit)}
          </p>
          <div className="mt-0.5 flex items-center gap-1.5 text-sky-600 dark:text-sky-400">
            {borderIcon}
            <span className="text-xs font-medium">{crossing.fromCountry} → {crossing.toCountry}</span>
          </div>
        </div>
      ));
    }
    const legDistance = firstDay.legDistances?.[segmentIndex];
    return legDistance == null ? null : (
      <p className="text-xs text-stone-500 dark:text-slate-400">
        {formatDistance(legDistance, settings.distanceUnit)}
      </p>
    );
  };

  const verticalRoute = (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-2">
        <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-green-600 dark:bg-green-400" />
        <p className="truncate text-base font-semibold text-stone-900 dark:text-white">{firstDay.originCity}</p>
      </div>
      {firstDay.legDistances?.[0] != null && (
        <div className="ml-1 border-l-2 border-dashed border-green-600/60 py-0.5 pl-3 dark:border-green-400/60">
          {renderLeg(0)}
        </div>
      )}
      {hasWaypoints && (
        <div className="ml-1 border-l-2 border-dashed border-green-600/60 dark:border-green-400/60">
          {(firstDay.waypoints ?? []).map((waypoint, index) => waypoint.trim() && (
            <React.Fragment key={`${waypoint}-${index}`}>
              <div className="flex items-center gap-2 py-0.5 pl-3">
                <div className="-ml-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-green-600/80 dark:bg-green-400/80" />
                <p className="truncate text-sm text-stone-700 dark:text-slate-300">{waypoint}</p>
              </div>
              {firstDay.legDistances?.[index + 1] != null && (
                <div className="py-0.5 pl-3">{renderLeg(index + 1)}</div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      <div className={`flex items-center gap-2 ${hasWaypoints ? 'pt-0.5' : ''}`}>
        <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-600 dark:bg-red-400" />
        <p className="truncate text-base font-semibold text-stone-900 dark:text-white">{firstDay.destinationCity}</p>
      </div>
    </div>
  );

  const metaBlock = (
    <>
      {trip.tag ? (
        <TagBadge tag={trip.tag} />
      ) : null}
      <p className="text-sm font-medium text-stone-800 dark:text-slate-100 leading-snug [overflow-wrap:anywhere] break-words">
        {trip.carMake} · {trip.fuelType}
      </p>
      <p className="text-xs text-stone-500 dark:text-slate-400">
        {isMultiDay
          ? `${formatDate(trip.dateFrom ?? firstDay.date)} — ${formatDate(trip.dateTo ?? lastDay.date)}`
          : formatDate(firstDay.date)}
      </p>
      {!isMultiDay && (
        <>
          <p className="text-xs text-stone-500 dark:text-slate-400">
            {formatDistance(firstDay.distance, settings.distanceUnit)}
            {firstDay.formattedDuration ? ` · ${firstDay.formattedDuration}` : ''}
          </p>
          <p className="text-xs text-stone-500 dark:text-slate-400">
            {formatNumber(firstDay.fuelUsed || 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {t.liters}
          </p>
        </>
      )}
      <p className={`text-base font-bold tabular-nums ${theme.price}`}>{priceLabel}</p>
    </>
  );

  const actionButtons = (
    <>
      {onView && (
        <button
          type="button"
          onClick={stop(() => isAuthenticated && onView(trip))}
          disabled={!isAuthenticated}
          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700 shrink-0 disabled:opacity-50"
          title={t.viewTrip}
        >
          <Eye className="h-4 w-4" />
        </button>
      )}
      {onEdit && (
        <button
          type="button"
          onClick={stop(() => isAuthenticated && onEdit(trip))}
          disabled={!isAuthenticated}
          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700 shrink-0 disabled:opacity-50"
          title={t.editTrip}
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={stop(() => {
            if (isAuthenticated && window.confirm(t.confirmDeleteTrip)) onDelete(trip.id);
          })}
          disabled={!isAuthenticated}
          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0 disabled:opacity-50"
          title={t.deleteTrip}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </>
  );

  if (!isMultiDay) {
    return (
      <TripRouteTile
        selected={selected}
        onSelect={onSelect}
        theme={theme}
        language={settings.language}
        routeIcon={Car}
        origin={null}
        destination={null}
        routeBody={verticalRoute}
        showEndpointLabels={false}
        meta={metaBlock}
        actions={actionButtons}
      />
    );
  }

  return (
    <TripRouteTile
      selected={selected}
      onSelect={onSelect}
      theme={theme}
      language={settings.language}
      routeIcon={Car}
      distanceLabel={formatDistance(totalDistance, settings.distanceUnit)}
      durationLabel={daysCountLabel}
      showEndpointLabels={false}
      origin={<p className={cityClass}>{firstDay.originCity}</p>}
      destination={<p className={cityClass}>{lastDay.destinationCity}</p>}
      meta={metaBlock}
      actions={actionButtons}
      extra={
        <button
          type="button"
          onClick={stop(() => setDaysExpanded((expanded) => !expanded))}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
          aria-expanded={daysExpanded}
          aria-controls={`car-trip-${trip.id}-days`}
        >
          {daysExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {daysCountLabel} · {formatDistance(totalDistance, settings.distanceUnit)}
        </button>
      }
      after={
        daysExpanded ? (
          <div
            id={`car-trip-${trip.id}-days`}
            className="border-t border-stone-100 dark:border-slate-700 bg-stone-50/70 dark:bg-slate-900/30"
          >
            {days.map((day: CarTripDay, index) => (
              <div
                key={day.id ?? `${day.date}-${index}`}
                className="flex items-center gap-2 px-4 py-2 border-b last:border-b-0 border-stone-100 dark:border-slate-700"
              >
                <span className="text-xs font-semibold text-stone-500 dark:text-slate-400 shrink-0">
                  {t.carTripDay} {index + 1}
                </span>
                <span className="text-xs text-stone-500 shrink-0">{formatDate(day.date)}</span>
                <span className="min-w-0 flex-1 flex items-center gap-1.5 text-sm text-stone-800 dark:text-slate-200">
                  <span className="truncate">{day.originCity}</span>
                  <CarRouteSep compact />
                  <span className="truncate">{day.destinationCity}</span>
                </span>
                <span className="text-xs text-amber-700 dark:text-amber-300 shrink-0 tabular-nums">
                  {formatDistance(day.distance, settings.distanceUnit)}
                </span>
              </div>
            ))}
            {onAddDay && (
              <div className="border-t border-stone-100 px-4 py-2 dark:border-slate-700">
                <button
                  type="button"
                  onClick={stop(() => isAuthenticated && onAddDay(trip))}
                  disabled={!isAuthenticated}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-100 px-2.5 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
                >
                  <Plus className="h-3.5 w-3.5" /> {t.addCarTripDay}
                </button>
              </div>
            )}
          </div>
        ) : null
      }
    />
  );
};
